import { useCallback } from 'react'
import { isAxiosError } from 'axios'
import type {
  AssociatedPartyType,
  CreateAssociatedPartyPayload,
} from '@/shared/hooks/api/associatedParties'

const MANUFACTURER_ROLE: AssociatedPartyType = 'MANUFACTURER'

type UseCreateAndAssignManufacturerParams = {
  currentManufacturerId?: string | null
  createAssociatedParty: (payload: CreateAssociatedPartyPayload) => Promise<{
    id: string
  }>
  unassignAssociatedPartyFromProduct: (payload: {
    associatedPartyId: string
    role: AssociatedPartyType
  }) => Promise<unknown>
  assignAssociatedPartyToProduct: (payload: {
    associatedPartyId: string
    role: AssociatedPartyType
  }) => Promise<unknown>
}

export type CreateAndAssignManufacturerResult =
  | 'success'
  | 'alreadyAssigned'
  | 'createFailed'
  | 'assignFailedAfterCreate'

export function useCreateAndAssignManufacturer({
  currentManufacturerId,
  createAssociatedParty,
  unassignAssociatedPartyFromProduct,
  assignAssociatedPartyToProduct,
}: UseCreateAndAssignManufacturerParams) {
  const createAndAssignManufacturer = useCallback(
    async (
      payload: CreateAssociatedPartyPayload
    ): Promise<CreateAndAssignManufacturerResult> => {
      let createdPartyId: string | null = null

      try {
        const createdParty = await createAssociatedParty(payload)
        createdPartyId = createdParty.id

        if (currentManufacturerId) {
          await unassignAssociatedPartyFromProduct({
            associatedPartyId: currentManufacturerId,
            role: MANUFACTURER_ROLE,
          })
        }

        await assignAssociatedPartyToProduct({
          associatedPartyId: createdParty.id,
          role: MANUFACTURER_ROLE,
        })

        return 'success'
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 409) {
          return 'alreadyAssigned'
        }

        if (createdPartyId) {
          return 'assignFailedAfterCreate'
        }

        return 'createFailed'
      }
    },
    [
      assignAssociatedPartyToProduct,
      createAssociatedParty,
      currentManufacturerId,
      unassignAssociatedPartyFromProduct,
    ]
  )

  return { createAndAssignManufacturer }
}
